<template>
  <v-app-bar
    v-if="!isAdminRoute"
    :class="['glass-header', { 'header-hidden': isHeaderHidden, 'header-solid': isPastHero }]"
    elevation="0"
    density="comfortable"
    flat
  >
    <div class="w-100">
      <div class="header-accent"></div>
      <div class="d-flex align-center py-3 header-inner">
        <RouterLink to="/" class="header-logo-link" @click="goHome">
          <img :src="logoUrl" alt="LINQFORCE Logo" width="178" height="52" fetchpriority="high" decoding="async" class="header-logo mr-3" />
        </RouterLink>
        <v-spacer />

        <nav v-if="mdAndUp && !isLegalRoute" class="d-flex align-center ga-4 topnav" :aria-label="locale === 'en' ? 'Primary navigation' : 'Hauptnavigation'">
          <v-btn variant="text" :class="{ 'nav-active': activeSection === 'home' }" @click="nav('home')">{{ t('nav.home') }}</v-btn>
          <v-btn variant="text" :class="{ 'nav-active': activeSection === 'problems' }" @click="nav('problems')">{{ t('nav.solutions') }}</v-btn>
          <v-btn variant="text" :class="{ 'nav-active': activeSection === 'blog' }" @click="nav('blog')">{{ t('nav.blog') }}</v-btn>
          <v-btn variant="text" :class="{ 'nav-active': activeSection === 'core' }" @click="nav('core')">{{ t('nav.core') }}</v-btn>
          <v-btn variant="text" :class="{ 'nav-active': activeSection === 'career' }" @click="nav('career')">{{ t('nav.career') }}</v-btn>
          <v-select
            :model-value="locale"
            :items="localeOptions"
            item-title="title"
            item-value="value"
            :menu-props="localeMenuProps"
            variant="outlined"
            density="compact"
            hide-details
            menu-icon="mdi-chevron-down"
            class="locale-select"
            aria-label="Sprache / Language"
            @update:model-value="onLocaleChange"
          />
          <v-btn color="primary" size="large" class="header-cta" @click="nav('contact')">{{ t('cta.demo') }}</v-btn>
        </nav>

        <div v-else-if="mdAndUp && isLegalRoute" class="d-flex align-center ga-4">
          <v-btn variant="text" @click="goHome">← {{ t('nav.home') }}</v-btn>
          <v-select
            :model-value="locale"
            :items="localeOptions"
            item-title="title"
            item-value="value"
            :menu-props="localeMenuProps"
            variant="outlined"
            density="compact"
            hide-details
            menu-icon="mdi-chevron-down"
            class="locale-select"
            aria-label="Sprache / Language"
            @update:model-value="onLocaleChange"
          />
        </div>

        <v-btn
          v-else
          icon
          class="mobile-menu-btn"
          :aria-label="drawerModel ? t('a11y.closeMenu') : t('a11y.openMenu')"
          @click="toggleDrawer"
        >
          <v-icon :class="['mobile-menu-icon', { 'is-open': drawerModel }]">
            {{ drawerModel ? 'mdi-close' : 'mdi-menu' }}
          </v-icon>
        </v-btn>
      </div>
    </div>
  </v-app-bar>

  <v-navigation-drawer v-if="!isAdminRoute" v-model="drawerModel" temporary location="right" scrim elevation="8">
    <div class="d-flex align-center px-4 py-3">
      <RouterLink to="/" class="d-flex align-center header-logo-link" @click="goHome">
        <img :src="logoUrl" alt="LINQFORCE Logo" width="75" height="22" loading="lazy" decoding="async" class="mr-2" />
        <strong>LINQFORCE</strong>
      </RouterLink>
    </div>
    <div class="px-4 pb-3">
      <v-select
        :model-value="locale"
        :items="localeOptions"
        item-title="title"
        item-value="value"
        :menu-props="localeMenuProps"
        variant="outlined"
        density="compact"
        hide-details
        menu-icon="mdi-chevron-down"
        class="locale-select locale-select-drawer"
        aria-label="Sprache / Language"
        @update:model-value="onLocaleChange"
      />
    </div>
    <v-divider />
    <v-list nav density="comfortable">
      <v-list-item prepend-icon="mdi-home" :title="t('nav.home')" @click="nav('home')" />
      <v-list-item prepend-icon="mdi-lightbulb-on-outline" :title="t('nav.solutions')" @click="nav('problems')" />
      <v-list-item prepend-icon="mdi-post-outline" :title="t('nav.blog')" @click="nav('blog')" />
      <v-list-item prepend-icon="mdi-cogs" :title="t('nav.core')" @click="nav('core')" />
      <v-list-item prepend-icon="mdi-briefcase-outline" :title="t('nav.career')" @click="nav('career')" />
    </v-list>
    <div class="px-4 pb-4 pt-2">
      <v-btn block color="primary" size="large" class="drawer-cta" @click="nav('contact')">
        {{ t('cta.demo') }}
      </v-btn>
    </div>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

type NavAnchor = 'home' | 'problems' | 'blog' | 'core' | 'career' | 'contact'

const props = defineProps<{
  isAdminRoute: boolean
  isHeaderHidden: boolean
  isPastHero: boolean
  logoUrl: string
  mdAndUp: boolean
  isLegalRoute: boolean
  activeSection: string
  localeOptions: ReadonlyArray<{ title: string; value: string }>
  locale: string
  drawer: boolean
}>()

const emit = defineEmits<{
  'update:drawer': [value: boolean]
  nav: [anchor: NavAnchor]
  'go-home': []
  'on-locale-change': [value: string]
}>()

const { t } = useI18n()

const localeMenuProps = {
  contentClass: 'locale-select-menu',
  minWidth: 104,
}

const drawerModel = computed({
  get: () => props.drawer,
  set: (value: boolean) => emit('update:drawer', value),
})

function nav(anchor: NavAnchor) {
  emit('nav', anchor)
}

function goHome() {
  emit('go-home')
}

function onLocaleChange(value: string) {
  emit('on-locale-change', value)
}

function toggleDrawer() {
  drawerModel.value = !drawerModel.value
}
</script>

<style scoped>
.drawer-cta {
  min-height: 48px;
}

.header-logo-link {
  display: inline-flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
}
</style>
